import { toast } from 'sonner';
import { useGameStore } from '@/lib/stores/gameStore';

const POLL_INTERVAL_MS = 60_000;
const VERSION_URL = '/version.txt';

// Mutable so tests can shorten the wait; production paths leave it at 10s.
let AUTO_RELOAD_DELAY_MS = 10_000;

let started = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
let promptShown = false;
let pendingReload = false;
let unsubGameStore: (() => void) | null = null;

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    return (await res.text()).trim();
  } catch {
    return null;
  }
}

function reloadNow() {
  if (reloadTimer !== null) {
    clearTimeout(reloadTimer);
    reloadTimer = null;
  }
  window.location.reload();
}

function inActiveSession(): boolean {
  // `gameSessionId` is set while a game-server session is connected and
  // cleared on session end. Treat any non-null value as "do not force a
  // reload right now" — mid-round reloads trash the in-flight intent and
  // unsaved chat composer text with no recovery path (PWA installs have no
  // URL bar to even read what happened). The deferred reload still fires
  // when the session ends.
  return useGameStore.getState().gameSessionId !== null;
}

function scheduleAutoReload() {
  // Cancellable timer so a follow-up state change (session start, manual
  // "Not now") can defer the reload. The previous bare `setTimeout(reload)`
  // had no handle, so a player who saw the toast mid-round had no way to
  // hold the reload off — and there was no PWA guard at all.
  reloadTimer = setTimeout(reloadNow, AUTO_RELOAD_DELAY_MS);
}

function cancelAutoReload() {
  if (reloadTimer !== null) {
    clearTimeout(reloadTimer);
    reloadTimer = null;
  }
}

function showReloadPrompt() {
  if (promptShown) return;
  promptShown = true;
  pendingReload = true;

  const reloadDescription = inActiveSession()
    ? 'Reloading after your current session ends so your turn isn’t lost. Click Reload now to apply immediately (in-flight chat will be discarded).'
    : 'Reloading in 10 seconds to apply the update.';

  toast('A new version is available', {
    description: reloadDescription,
    duration: Number.POSITIVE_INFINITY,
    action: { label: 'Reload now', onClick: reloadNow },
  });

  if (!inActiveSession()) {
    scheduleAutoReload();
  }
  // Always watch the session boundary, regardless of which side we started
  // on. Covers both transitions:
  //  - In-session at prompt time: fire the deferred reload when the session
  //    ends (gameSessionId → null).
  //  - Out-of-session at prompt time: if the player starts a game inside
  //    the 10s window, cancel the armed timer so the reload doesn't trash
  //    the new session mid-round.
  // `subscribe(selector, listener)` only fires when the selected value
  // changes, so we don't have to debounce.
  unsubGameStore = useGameStore.subscribe(
    (s) => s.gameSessionId,
    (gameSessionId) => {
      if (gameSessionId === null && pendingReload && reloadTimer === null) {
        scheduleAutoReload();
      } else if (gameSessionId !== null) {
        // Player entered (or re-entered) a session — hold off the reload.
        cancelAutoReload();
      }
    }
  );
}

async function check() {
  const remote = await fetchRemoteBuildId();
  if (!remote) return;
  if (remote !== __BUILD_ID__) {
    showReloadPrompt();
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }
}

export function startVersionCheck() {
  if (started) return;
  started = true;
  if (__BUILD_ID__.startsWith('dev-')) return;
  pollTimer = setInterval(check, POLL_INTERVAL_MS);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  void check();
}

// Test-only: reset module state so the next `startVersionCheck()` runs
// fresh. Exposed under a guarded global symbol rather than as an export so
// production callers can't reach it.
declare global {
  var __resetVersionCheckForTest__: (() => void) | undefined;

  var __setVersionCheckReloadDelayForTest__: ((ms: number) => void) | undefined;
}
if (typeof globalThis !== 'undefined') {
  globalThis.__resetVersionCheckForTest__ = () => {
    started = false;
    promptShown = false;
    pendingReload = false;
    cancelAutoReload();
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (unsubGameStore) {
      unsubGameStore();
      unsubGameStore = null;
    }
  };
  globalThis.__setVersionCheckReloadDelayForTest__ = (ms: number) => {
    AUTO_RELOAD_DELAY_MS = ms;
  };
}
